import React, { useState } from 'react';
import { proxiedImage, proxiedSrcSet } from '../utils/imageProxy';

/**
 * Drop-in <img> replacement that:
 *   1. Routes the URL through our /api/img proxy (WebP + resize).
 *   2. Shows a shimmering skeleton placeholder until the image decodes.
 *   3. Crossfades to the loaded image so the transition feels premium.
 *
 * Why a wrapper instead of CSS-only:
 *   The shimmer needs to disappear the moment the browser fires `onLoad`
 *   for the real image. Pure CSS can't observe that — React state can.
 *
 * Props:
 *   src       — raw image URL (will be proxied automatically)
 *   alt       — required for accessibility & SEO
 *   width     — pick from {200,400,600,900,1200,1600}; controls proxy bucket
 *   sizes     — optional <img sizes> hint for responsive srcset
 *   srcset    — boolean; emit a multi-width srcset for retina/responsive
 *   style     — passthrough; applied to the wrapper
 *   imgStyle  — passthrough; applied to the <img>
 *   testId    — data-testid for the wrapper
 */
const SmartImage = ({
  src,
  alt = '',
  width = 800,
  sizes,
  srcset = false,
  style,
  imgStyle,
  testId,
  priority = false,
  ...imgProps
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const proxied = proxiedImage(src, width);
  const srcSetAttr = srcset ? proxiedSrcSet(src) : undefined;

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#F0EAE0',
        ...style,
      }}
      data-testid={testId}
    >
      {/* Shimmer placeholder — invisible once the real image is loaded */}
      {!loaded && !errored && (
        <div style={shimmerStyle} aria-hidden="true" />
      )}
      {!errored && (
        <img
          src={proxied}
          srcSet={srcSetAttr}
          sizes={sizes}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
            ...imgStyle,
          }}
          {...imgProps}
        />
      )}
      {errored && (
        <div style={errorStyle} aria-hidden="true">
          <span style={{ fontSize: '0.75rem', color: '#8C8B88' }}>📷</span>
        </div>
      )}
    </div>
  );
};

// Inject the shimmer keyframes once per app (idempotent).
if (typeof document !== 'undefined' && !document.getElementById('smart-image-shimmer-kf')) {
  const style = document.createElement('style');
  style.id = 'smart-image-shimmer-kf';
  style.textContent = `
    @keyframes smartImageShimmer {
      0% { background-position: -468px 0; }
      100% { background-position: 468px 0; }
    }
  `;
  document.head.appendChild(style);
}

const shimmerStyle = {
  position: 'absolute',
  inset: 0,
  backgroundColor: '#F0EAE0',
  backgroundImage:
    'linear-gradient(90deg, #F0EAE0 0%, #FAF6EE 50%, #F0EAE0 100%)',
  backgroundSize: '936px 100%',
  animation: 'smartImageShimmer 1.4s linear infinite',
};

const errorStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F0EAE0',
};

export default SmartImage;
